import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd, PRIMARY_OUTLET, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { Breadcrumb } from './breadcrumb';
import { CourseService } from '../../../../pages/course/course.service';
import { AlertService } from '../../../alert/alert.service';
import { LessonService } from '../../../../pages/lesson/lesson.service';
import { ExerciseService } from '../../../../pages/exercise/exercise.service';
import { ProblemService } from '../../../../shared/problem.service';
import { SolutionService } from '../../../../pages/problem/solution/solution.service';
import { PassageService } from '../../../../pages/passage/passage.service';
import { TopicService } from '../../../../pages/topic/topic.service';
import { ArraySortPipe } from '../../../pipes/array-sort.pipe';

type BreadcrumbAction = 'Create' | 'Edit' | 'Manage' | 'Login';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule, MatButtonModule, MatIconModule, ArraySortPipe],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class BreadcrumbComponent implements OnInit {
  public breadcrumbs: Breadcrumb[] = [];
  public action: BreadcrumbAction;

  ROUTE_DATA_BREADCRUMB = 'breadcrumb';

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private courseService: CourseService,
    private lessonService: LessonService,
    private exerciseService: ExerciseService,
    private problemService: ProblemService,
    private solutionService: SolutionService,
    private passageService: PassageService,
    private alertService: AlertService,
    private topicService: TopicService,
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const root: ActivatedRoute = this.activatedRoute.root;
        this.breadcrumbs = [];
        this.action = null;
        this.breadcrumbs = this.getBreadcrumbs(root, 0);
      });
  }

  private getBreadcrumbs(route: ActivatedRoute, deep: number, url: string = ''): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;
    if (children.length === 0) {
      return this.breadcrumbs;
    }

    for (const child of children) {
      if (child.outlet !== PRIMARY_OUTLET) {
        continue;
      }

      if (!child.snapshot.data.hasOwnProperty(this.ROUTE_DATA_BREADCRUMB)) {
        continue;
      }

      const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
      url += `/${routeURL}`;

      const breadcrumbData = child.snapshot.data['breadcrumb'];
      const value = breadcrumbData['value'];
      const bcType = breadcrumbData['bcType'];
      const type = breadcrumbData['type'];
      const action = breadcrumbData['action'];

      this.action = action ? action : this.action;

      if (bcType === 'Display') {
        this.setBreadcrumbDisplay(url, type, deep);
      } else if (bcType === 'LookUp') {
        this.setBreadcrumbLookUp(type, value, child, url, deep);
      }

      return this.getBreadcrumbs(child, deep + 1, url);
    }
  }

  private setBreadcrumbLookUp(type: string, value: string, route: ActivatedRoute, url: string, deep: number) {
    let listBreadCrumb: Breadcrumb;
    let breadCrumb: Breadcrumb;
    switch (type) {
      case 'Course': {
        this.courseService.getCourse(+route.snapshot.params['courseId']).subscribe(
          (course) => {
            listBreadCrumb = new Breadcrumb('Courses', '/courses', deep);
            breadCrumb = new Breadcrumb(course.title, url + '/manage', deep);
            this.breadcrumbs = [...this.breadcrumbs, listBreadCrumb, breadCrumb];
          },
          (err) => this.alertService.error(err),
        );
        break;
      }
      case 'Lesson': {
        this.lessonService.getLesson(+route.snapshot.params['lessonId']).subscribe(
          (lesson) => {
            listBreadCrumb = new Breadcrumb('Lessons', url + '/manage', deep);
            breadCrumb = new Breadcrumb(lesson.title, url + '/manage', deep);
            this.breadcrumbs = [...this.breadcrumbs, listBreadCrumb, breadCrumb];
          },
          (err) => this.alertService.error(err),
        );
        break;
      }
      case 'Exercise': {
        this.exerciseService.getExercise(+route.snapshot.params['exerciseId']).subscribe(
          (exercise) => {
            listBreadCrumb = new Breadcrumb('Exercise', url + '/manage', deep);
            breadCrumb = new Breadcrumb(exercise.title, url + '/manage', deep);
            this.breadcrumbs = [...this.breadcrumbs, listBreadCrumb, breadCrumb];
          },
          (err) => this.alertService.error(err),
        );
        break;
      }
      case 'Problem': {
        this.problemService.getProblem(+route.snapshot.params['problemId']).subscribe(
          (problem) => {
            listBreadCrumb = new Breadcrumb('Problems', '/problems', deep);
            breadCrumb = new Breadcrumb(problem.title, url + '/manage', deep);
            this.breadcrumbs = [...this.breadcrumbs, listBreadCrumb, breadCrumb];
          },
          (err) => this.alertService.error(err),
        );
        break;
      }
      case 'Solution': {
        this.solutionService.getSolution(+route.snapshot.params['solutionId']).subscribe(
          (solution) => {
            listBreadCrumb = new Breadcrumb('Solution', url + '/manage', deep);
            breadCrumb = new Breadcrumb(solution.title, url + '/edit', deep);
            this.breadcrumbs = [...this.breadcrumbs, listBreadCrumb, breadCrumb];
          },
          (err) => this.alertService.error(err),
        );
        break;
      }
      case 'Passage': {
        this.passageService.getPassage(+route.snapshot.params['passageId']).subscribe(
          (passage) => {
            listBreadCrumb = new Breadcrumb('Passages', '/passages', deep);
            breadCrumb = new Breadcrumb(passage.title, url + '/edit', deep);
            this.breadcrumbs = [...this.breadcrumbs, listBreadCrumb, breadCrumb];
          },
          (err) => this.alertService.error(err),
        );
        break;
      }
      case 'Topic': {
        this.topicService.getTopic(+route.snapshot.params['topicId']).subscribe(
          (topic) => {
            listBreadCrumb = new Breadcrumb('Topics', '/topics', deep);
            breadCrumb = new Breadcrumb(topic.displayName, url + '/edit', deep);
            this.breadcrumbs = [...this.breadcrumbs, listBreadCrumb, breadCrumb];
          },
          (err) => this.alertService.error(err),
        );
        break;
      }
    }
  }

  private setBreadcrumbDisplay(url: string, type: string, deep: number) {
    let breadcrumb: Breadcrumb;
    switch (type) {
      case 'Course': {
        breadcrumb = new Breadcrumb('Courses', `/courses`);
        this.breadcrumbs = [...this.breadcrumbs, breadcrumb];
        break;
      }
      case 'Lesson': {
        breadcrumb = new Breadcrumb('Lessons', url, deep);
        this.breadcrumbs = [...this.breadcrumbs, breadcrumb];
        break;
      }
      case 'Exercise': {
        breadcrumb = new Breadcrumb('Exercises', url, deep);
        this.breadcrumbs = [...this.breadcrumbs, breadcrumb];
        break;
      }
      case 'Problem': {
        breadcrumb = new Breadcrumb('Problems', '/problems', deep);
        this.breadcrumbs = [...this.breadcrumbs, breadcrumb];
        break;
      }
      case 'Solution': {
        breadcrumb = new Breadcrumb('Solutions', url, deep);
        this.breadcrumbs = [...this.breadcrumbs, breadcrumb];
        break;
      }
      case 'Topic': {
        breadcrumb = new Breadcrumb('Topics', '/topics', deep);
        this.breadcrumbs = [...this.breadcrumbs, breadcrumb];
        break;
      }
      case 'Passage': {
        breadcrumb = new Breadcrumb('Passages', '/passages', deep);
        this.breadcrumbs = [...this.breadcrumbs, breadcrumb];
        break;
      }
    }
  }
}
